import isNumber from 'lodash/isNumber';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  clearAllCheckoutTracking,
  resetMixpanel,
} from '@monorepo/client-common';
import { AffiliateUserWithDetails, UserPayload } from '@monorepo/client-types';
import {
  AdminTokenKey,
  FeatureFlagAccessTokenKey,
  IdpProviderKey,
  IsLoggedInKey,
  IsLoggedOutKey,
  isUserBlocked,
  NOT_AVAILABLE,
  Role,
  TokenSyncStatusKey,
  User,
} from '@monorepo/types';
import { getEnv, getRoot } from '@hub/helpers/getters.store';
import { EnvService } from '@hub/services/core/env-service';
import { ERROR_TYPES, UIErrorService } from '@hub/services/core/error-service';

const reactEnv = EnvService.appEnv || 'development';
const loginAttemptsKey = 'loginAttempts';
const registrationAttemptsKey = 'registrationAttempts';
const prefix = reactEnv === 'production' ? '' : `${reactEnv.slice(0, 3)}-`;

export const KYC_TOKEN_KEY = `${prefix}kyc-token`;

export default class UserStore {
  @observable
  isLoggedIn = false;

  @observable
  currentUser: User = {} as User;

  @observable
  affiliationCommissionsAvailable = 0;

  @observable
  isImpersonated = false;

  @observable
  affiliateDashboard: AffiliateUserWithDetails = {} as AffiliateUserWithDetails;

  @observable
  loginAttempts = 0;

  @observable
  registrationAttempts = 0;

  constructor() {
    makeObservable(this);
  }

  getCountry() {
    return this.currentUser.country || NOT_AVAILABLE;
  }

  getDisplayName({
    firstName,
    lastName,
    userName,
  }: {
    userName?: string;
    firstName?: string;
    lastName?: string;
  }) {
    if (userName) {
      return userName;
    }

    const lastNameInitial = lastName ? `${lastName[0]}.` : '';
    const toFirstName = firstName ? firstName.trim() : '';
    const result = `${toFirstName} ${lastNameInitial}`.trim();

    return result || NOT_AVAILABLE;
  }

  async updateCredits() {
    try {
      const {
        apiFactory: { creditsService },
      } = getEnv();

      const updatedCredits = await creditsService.getCredits();

      runInAction(() => {
        this.currentUser.creditBalance = updatedCredits;
      });
    } catch (e) {
      console.error(`Failed updating credits`, e);
    }
  }

  async getCreditsHistory() {
    try {
      const {
        apiFactory: { creditsService },
      } = getEnv();

      return await creditsService.getCreditsHistory();
    } catch (e) {
      console.error(`Failed getting credits history`, e);
      throw e;
    }
  }

  async updateLogins() {
    try {
      const {
        apiFactory: { accountService },
      } = getEnv();

      const logins = await accountService.getLogins();

      runInAction(() => {
        this.currentUser.tsUsers = logins;
      });

      return logins;
    } catch (e) {
      console.error(`Failed updating credits`, e);
    }
  }

  async otpRegister(
    userData: UserPayload,
    options?: { registeredViaContestSignup?: boolean },
  ) {
    try {
      const {
        apiFactory: { authenticationService },
      } = getEnv();
      const response = await authenticationService.otpRegister(
        userData,
        options,
      );
      // Reset registration attempts on successful registration
      this.resetRegistrationAttempts();
      return response?.userId;
    } catch (e) {
      // Increment registration attempts on error (same pattern as login)
      this.incrementRegistrationAttempts();
      console.error(e);
      throw e;
    }
  }

  async logout() {
    const {
      apiFactory: { authenticationService },
    } = getEnv();

    const {
      dataStore: { realTimeStore },
    } = getRoot();

    // Reset Mixpanel identity FIRST
    resetMixpanel();

    clearAllCheckoutTracking();

    await authenticationService.logout();

    localStorage.removeItem(IsLoggedInKey);
    localStorage.removeItem(IdpProviderKey);
    localStorage.removeItem(TokenSyncStatusKey);
    localStorage.setItem(IsLoggedOutKey, 'true');

    // Clear admin token from sessionStorage on logout
    sessionStorage.removeItem(AdminTokenKey);
    // Synchronously remove the X-Admin-Token axios header so no further requests
    // carry the stale impersonation credential before the page navigates away.
    getEnv().apiFactory.clearAdminToken();
    // Clear feature flag access token on logout
    localStorage.removeItem(FeatureFlagAccessTokenKey);

    // Disconnect real-time updates
    realTimeStore.disconnect();

    getRoot().uiStore.clearInitialUrl();

    // Redirect to logout URL
    window.location.href = getRoot().uiStore.getLogoutUrl;
  }

  async refreshToken() {
    const {
      apiFactory: { authenticationService },
    } = getEnv();
    await authenticationService.refreshToken();
  }

  async getUser(): Promise<User> {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();
      const user = await userService.getUser();
      this.setCurrentUser(user);
      return user;
    } catch (e) {
      this.incrementLoginAttempts();
      console.error(e);
      throw e;
    }
  }

  async createKycToken() {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();
      const kycToken = await userService.getKycAccessToken();
      localStorage.setItem(KYC_TOKEN_KEY, kycToken);
      return kycToken;
    } catch (e) {
      console.log('Error occurred on creation of token', e);
    }
  }

  getKycToken() {
    return localStorage.getItem(KYC_TOKEN_KEY);
  }

  async isValidUsername(username: string) {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();

      return await userService.isValidUsername(username);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async update(userDataToUpdate: Partial<User>) {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();
      const updatedUser = await userService.updateUser(userDataToUpdate);
      this.setCurrentUser({ ...this.currentUser, ...updatedUser });
      return updatedUser;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async uploadProfileImage(file: FormData) {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();
      const imageUrl = await userService.uploadProfilePicture(file);
      runInAction(() => {
        this.currentUser.profileUrl = imageUrl;
      });
      return imageUrl;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteProfileImage() {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();
      const response = await userService.deleteProfilePicture();
      runInAction(() => {
        this.currentUser.profileUrl = undefined;
      });
      return response;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async registerAsAffiliate(): Promise<number | null> {
    try {
      const {
        apiFactory: { affiliationService },
      } = getEnv();
      // Service returns number directly (affiliateId) after extracting response.data
      const affiliateId = await affiliationService.registerAsAffiliate();

      if (typeof affiliateId === 'number') {
        runInAction(() => {
          this.setCurrentUser({
            ...this.currentUser,
            affiliateId: affiliateId.toString(),
          });
        });
        return affiliateId;
      }
      return null;
    } catch (e) {
      new UIErrorService({
        name: ERROR_TYPES.API,
        message: 'Failed to register as affiliate',
        data: { error: e },
      });
      return null;
    }
  }

  async getUserVerificationData() {
    try {
      const {
        apiFactory: { userService },
      } = getEnv();

      const userVerificationData = await userService.getUserVerificationData();

      runInAction(() => {
        this.setCurrentUser({
          ...this.currentUser,
          verificationData: userVerificationData,
        });
      });
    } catch (e) {
      console.error(`Failed updating user`, e);
    }
  }

  async getUserAffiliationDashboard() {
    try {
      const {
        apiFactory: { affiliationService },
      } = getEnv();

      const affiliationDashboard =
        await affiliationService.getUserAffiliationDashboard();
      runInAction(() => {
        this.affiliateDashboard = affiliationDashboard;
      });
    } catch (e) {
      console.error(`Failed updating user`, e);
    }
  }

  @action
  setCurrentUser(data: User) {
    this.currentUser = data;
  }

  @action
  updatePaymentData(data: User['paymentData']) {
    this.currentUser.paymentData = {
      ...this.currentUser.paymentData,
      ...data,
    };
  }

  @action
  setLoginState(loginState: boolean) {
    this.isLoggedIn = loginState;
  }

  @action
  toggleImpersonation() {
    this.isImpersonated = true;
  }

  @computed
  get fullName() {
    return `${this.currentUser.firstName?.trim()} ${this.currentUser.lastName?.trim()}`;
  }

  hasImpersonationAccess({ roles }: { roles: Role[] }): boolean {
    if (!this.isImpersonated) {
      return true;
    }

    const impersonationRole =
      this.currentUser.impersonatedDetails?.impersonatedAccess.role;

    if (!impersonationRole) {
      return false;
    }

    return roles.includes(impersonationRole);
  }

  @computed
  get verificationRequired() {
    return (
      this.currentUser.verificationData &&
      isUserBlocked(this.currentUser.verificationData)
    );
  }

  async getAffiliateAvailableSum() {
    try {
      const {
        apiFactory: { affiliationService },
      } = getEnv();

      return affiliationService.getAffiliateAvailableCommissions();
    } catch (e) {
      console.error(`Failed getting affiliate available sum`, e);
      return 0;
    }
  }

  async getPublicProfile(publicId?: string) {
    const {
      apiFactory: { userService },
    } = getEnv();

    return await userService.getPublicProfile(publicId);
  }

  @action
  incrementLoginAttempts() {
    const attempts = this.getLoginAttempts() + 1;
    this.loginAttempts = attempts;
    localStorage.setItem(loginAttemptsKey, attempts.toString());
  }

  @action
  resetLoginFailureAttempts() {
    localStorage.removeItem(loginAttemptsKey);
    this.loginAttempts = 0;
  }

  @action
  getLoginAttempts() {
    let storedAttempts = Number(localStorage.getItem(loginAttemptsKey));

    if (!isNumber(storedAttempts)) {
      storedAttempts = 0;
    }

    this.loginAttempts = storedAttempts;
    return this.loginAttempts;
  }

  @action
  incrementRegistrationAttempts() {
    const attempts = this.getRegistrationAttempts() + 1;
    this.registrationAttempts = attempts;
    localStorage.setItem(registrationAttemptsKey, attempts.toString());
  }

  @action
  resetRegistrationAttempts() {
    localStorage.removeItem(registrationAttemptsKey);
    this.registrationAttempts = 0;
  }

  @action
  getRegistrationAttempts() {
    let storedAttempts = Number(localStorage.getItem(registrationAttemptsKey));

    if (!isNumber(storedAttempts)) {
      storedAttempts = 0;
    }

    this.registrationAttempts = storedAttempts;
    return this.registrationAttempts;
  }
}
